import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useI18n } from '../../../context/I18nContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { useIntegrationService } from '../../../services/integration.service';
import { Platform } from '../../../services/integration.types';
import IntegrationExchangeApiFormComponent from './ExchangeApi';
import IntegrationExchangeCsvFormComponent from './ExchangeCsv';

interface IntegrationExchangeFormComponent {
    platform: Platform;
    selectedImportType: string | null;
    onJobCreated: () => void;
}

const IntegrationExchangeFormComponent: React.FC<IntegrationExchangeFormComponent> = ({
    platform,
    selectedImportType,
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const { currentLanguage } = useI18n();
    const integrationService = useIntegrationService();
    const [selectedTab, setSelectedTab] = useState(0);

    const { data: importers, isLoading } = useQuery({
        queryKey: ['integrationPlatformImporters', platform.name],
        queryFn: async () => {
            const result = await integrationService.getPlatformImporters(platform.name, currentLanguage);
            const nonLegacyImporters = result.filter(
                (importer) => !importer.displayName.toLocaleLowerCase().includes('legacy')
            );

            for (const importer of nonLegacyImporters) {
                if (importer.customText) {
                    const matches = importer.customText.match(/{{(.*?)}}/);
                    if (matches && matches[1]) {
                        const ipAssignmentName = matches[1];
                        const ipAssignmentResult = await integrationService.getIpAssignment(ipAssignmentName);
                        importer.customText = importer.customText.replace(
                            `{{${ipAssignmentName}}}`,
                            ipAssignmentResult.ip_address
                        );
                    }
                }
            }
            return nonLegacyImporters;
        },
    });

    useEffect(() => {
        if (importers && selectedImportType) {
            // Find the index of the importer with matching type
            const index = importers.findIndex(
                (importer) => importer.type.toLowerCase() === selectedImportType.toLowerCase()
            );

            // Set the selected tab if found, otherwise keep current selection
            if (index !== -1) {
                setSelectedTab(index);
            }
        }
    }, [importers, selectedImportType]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    if (!platform)
        return (
            <Typography variant="h6" gutterBottom>
                {t('common.integration.selectPlatform')}
            </Typography>
        );

    return (
        <>
            {isLoading || importers === undefined ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : importers?.length === 0 ? (
                <Alert severity="warning">
                    {t('integrations.exchange.noImporters', { platform: platform.display_name })}
                </Alert>
            ) : (
                <>
                    {/* Only show tabs when there are multiple importer types */}
                    {importers.length > 1 && (
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            aria-label={t('integrations.exchange.importerTabs')}
                        >
                            {importers?.map((importer, index) => (
                                <Tab key={index} label={importer.type.toUpperCase()} />
                            ))}
                        </Tabs>
                    )}
                    {selectedTab !== null && (
                        <>
                            {importers[selectedTab].type === 'api' ? (
                                <IntegrationExchangeApiFormComponent
                                    platform={platform}
                                    importer={importers[selectedTab]}
                                    onJobCreated={() => onJobCreated()}
                                />
                            ) : (
                                <IntegrationExchangeCsvFormComponent
                                    platform={platform}
                                    importer={importers[selectedTab]}
                                    onJobCreated={() => onJobCreated()}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default IntegrationExchangeFormComponent;
